import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSupabaseConfigured } from '@/lib/supabase';
import { joinInviteCode } from '@/lib/collaborators-api';
import { useStore } from '@/utils/store';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import { Link, CheckCircle2 } from 'lucide-react';

export default function JoinResourceModal({ open, onClose }) {
  const { t } = useTranslation();
  const addToast = useStore((s) => s.addToast);

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleClose = () => {
    setCode('');
    setError('');
    setResult(null);
    onClose();
  };

  const handleJoin = async () => {
    if (code.length < 4) return;
    if (!isSupabaseConfigured()) return;

    setLoading(true);
    setError('');

    try {
      const data = await joinInviteCode(code.trim());
      setResult(data);
      addToast({ type: 'success', message: t('collaborators.joinSuccess') });
    } catch (err) {
      setError(err.message || t('collaborators.joinError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title={t('collaborators.joinTitle')} footer={
      result ? (
        <Button className="w-full" onClick={handleClose}>
          {t('common.save')}
        </Button>
      ) : (
        <Button className="w-full" onClick={handleJoin} loading={loading} disabled={code.length < 4}>
          <Link size={16} className="mr-1.5" />
          {t('collaborators.join')}
        </Button>
      )
    }>
      <div className="space-y-4">
        {result ? (
          <div className="text-center py-4 space-y-3">
            <div className="w-14 h-14 rounded-full bg-success-100 dark:bg-success-500/10 mx-auto flex items-center justify-center">
              <CheckCircle2 size={28} className="text-success-600 dark:text-success-400" />
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {t('collaborators.joinSuccess')}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {result.resource_type === 'property'
                ? t('collaborators.resourceProperty')
                : t('collaborators.resourceBusiness')}
              : <span className="font-medium text-gray-900 dark:text-white">{result.resource_name}</span>
            </div>
            <div className="text-[10px] font-bold px-2 py-1 rounded-md bg-brand-100 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 inline-block">
              {t(`collaborators.role.${result.role}`)}
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('collaborators.joinDescription')}
            </p>

            {error && (
              <div className="p-3 rounded-xl bg-danger-50 dark:bg-danger-500/10 text-danger-700 dark:text-danger-400 text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
                {t('collaborators.enterCode')}
              </label>
              <input
                type="text"
                className="input text-center font-mono text-lg tracking-[0.2em] uppercase"
                placeholder={t('collaborators.codePlaceholder')}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase())}
                maxLength={8}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
